import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Anchor,
    Body1,
    Body2,
    Flex,
    IconCached,
    IconCheckCircle,
    IconEvent,
    IconEventBusy,
    IconMedication,
    IconNoteAlt,
    IconNumbers,
    IconStethoscope,
    IconUpdate,
    Stack,
    formatPhoneNumber,
} from '@myphx-engineering/phx-design-system';

import { ConditionalComponent } from '../../../components/utility/ConditionalComponent';
import { getFragment } from '../../../graphql';
import type {
    DigitallyReceivedPrescriberOrderInfoFragment,
    PriceQuoteInfoFragment,
} from '../../../graphql/generated/graphql';
import { formatDateString } from '../../../util';

import classes from './prescriber-order-details.module.css';

export type PrescriberOrderDetailsProps = {
    prescriberOrder: DigitallyReceivedPrescriberOrderInfoFragment;
    priceQuote?: PriceQuoteInfoFragment;
    showQuantity?: boolean;
    showPrescriber?: boolean;
    showRxImage?: boolean;
};

const DataItem = ({
    description,
    icon,
}: {
    description: ReactNode;
    icon: ReactNode;
}) => {
    return (
        <Flex gap="xs">
            {icon}
            {description}
        </Flex>
    );
};

export const PrescriberOrderDetails = ({
    prescriberOrder,
    priceQuote,
    showQuantity = false,
    showPrescriber = true,
    showRxImage = false,
}: PrescriberOrderDetailsProps) => {
    const { t, i18n } = useTranslation();

    const writtenDate = formatDateString(
        prescriberOrder.writtenDate,
        i18n.language,
        { timeZone: 'UTC' }
    );
    const expirationDate = prescriberOrder.expirationDate
        ? formatDateString(prescriberOrder.expirationDate, i18n.language, {
              timeZone: 'UTC',
          })
        : null;

    const validInstructions =
        prescriberOrder.productInstruction?.filter(Boolean);
    const instructions = validInstructions?.join(' ');

    const prescriber = getFragment(prescriberOrder.prescriber);
    const prescriberPhone = prescriber.phoneNumber;

    const ndc =
        priceQuote?.product &&
        priceQuote.product.__typename !== 'UnknownProduct'
            ? getFragment(priceQuote.product).id
            : getFragment(prescriberOrder.prescribedProduct).id;

    const paEffectiveTo = getFragment(
        getFragment(prescriberOrder.applicablePriorAuthorization)
            ?.effectiveDates
    )?.effectiveTo;
    const paValidDate = paEffectiveTo ? formatDateString(paEffectiveTo) : null;

    return (
        <Stack gap="sm" className={classes['prescriber-order-details']}>
            {showRxImage ? (
                // oxlint-disable-next-line jsx-a11y/alt-text -- tracked in docs/oxlint-violations.md
                <img
                    src="/static/rx-details/rx-details.svg"
                    aria-hidden="true"
                    height="150"
                    width="135"
                />
            ) : null}
            <ConditionalComponent condition={showPrescriber}>
                <DataItem
                    data-testid="prescription-details-prescriber"
                    description={
                        <Stack gap={0}>
                            <Body1>
                                {t('order.prescriber', {
                                    name: prescriber.fullNameWithTitle,
                                })}
                            </Body1>
                            {prescriberPhone ? (
                                <Anchor textDecoration="underline">
                                    {formatPhoneNumber(prescriberPhone)}
                                </Anchor>
                            ) : null}
                        </Stack>
                    }
                    icon={<IconStethoscope color="secondary" />}
                />
            </ConditionalComponent>
            <ConditionalComponent
                condition={Boolean(validInstructions?.length)}
            >
                <DataItem
                    data-testid="prescription-details-directions"
                    description={<Body1>{instructions}</Body1>}
                    icon={<IconNoteAlt color="secondary" />}
                />
            </ConditionalComponent>
            <ConditionalComponent condition={showQuantity}>
                <DataItem
                    data-testid="prescription-details-quantity"
                    description={
                        <Body1>
                            {t('orderDetails.quantity', {
                                quantity: prescriberOrder.quantity,
                            })}
                        </Body1>
                    }
                    icon={<IconMedication color="secondary" />}
                />
            </ConditionalComponent>
            <ConditionalComponent
                condition={Boolean(prescriberOrder.daysSupply)}
            >
                <DataItem
                    data-testid="prescription-details-day-supply"
                    description={
                        <Body1>
                            {t('drugDetails.daysSupply', {
                                count: prescriberOrder.daysSupply!,
                            })}
                        </Body1>
                    }
                    icon={<IconUpdate color="secondary" />}
                />
            </ConditionalComponent>
            <DataItem
                data-testid="prescription-details-refills"
                description={
                    <Body1>
                        {t('order.refillCount', {
                            count: prescriberOrder.numberOfRefills,
                        })}
                    </Body1>
                }
                icon={<IconCached color="secondary" />}
            />
            <ConditionalComponent condition={!!paValidDate}>
                <DataItem
                    data-testid="prescription-details-pa-valid"
                    description={
                        <Body1>
                            {t('order.paValid', {
                                paValidDate,
                            })}
                        </Body1>
                    }
                    icon={<IconCheckCircle color="secondary" />}
                />
            </ConditionalComponent>
            <DataItem
                data-testid="prescription-details-written-date"
                description={
                    <Body1>{t('order.writtenOn', { date: writtenDate })}</Body1>
                }
                icon={<IconEvent color="secondary" />}
            />
            <ConditionalComponent condition={expirationDate !== null}>
                <DataItem
                    data-testid="prescription-details-expiration-date"
                    description={
                        <Body1>
                            {t('order.expiresOn', { date: expirationDate })}
                        </Body1>
                    }
                    icon={<IconEventBusy color="secondary" />}
                />
            </ConditionalComponent>
            <ConditionalComponent condition={!!ndc}>
                <DataItem
                    data-testid="prescription-details-ndc"
                    description={
                        <Body1>
                            {t('order.ndc', {
                                ndc,
                            })}
                        </Body1>
                    }
                    icon={<IconNumbers color="secondary" />}
                />
            </ConditionalComponent>
            <Body2>{t('order.officialPrescription')}</Body2>
        </Stack>
    );
};
